import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import {
  fetchActiveSeason,
  listSeasons,
  setActiveSeason as apiSetSeason,
  setEditUnlock as apiSetEditUnlock,
} from './api';
import { isSeasonCompleted } from './computedStatus';
import {
  fetchSharedSeason,
  fetchSharedSeasonConfig,
} from '@/lib/http/sharedQueries';
import { isPublicPath } from '@/lib/auth/publicPaths';
import { getSeasonConfig } from '@/lib/planning/api';
import type { ActiveSeason, SeasonSummary } from './types';
import type { SeasonVisibilityWindow } from './visibility';

export type { SeasonVisibilityWindow } from './visibility';

interface SeasonContextValue {
  season: ActiveSeason | null;
  seasons: SeasonSummary[];
  /**
   * Окно видимости сезонов из настройки компании (AK-790 re-land #673, UAT
   * баг 1): `visible_past/future_years` уважал только /planning, а
   * SeasonPicker в шапке показывал все 51 сезон сида. `null` — настройка не
   * загружена или недоступна (нет компании / несколько без выбора): селектор
   * показывает всё, как раньше. Живёт здесь, а не в селекторе, чтобы у
   * «одной настройки» был один запрос, а не по одному на потребителя.
   */
  visibility: SeasonVisibilityWindow | null;
  loading: boolean;
  setSeasonByCode: (code: string) => Promise<void>;
  setEditUnlock: (on: boolean) => Promise<void>;
  refetch: () => Promise<void>;
}

const SeasonContext = createContext<SeasonContextValue | null>(null);

export function SeasonProvider({
  children,
  companyId,
}: {
  children: ReactNode;
  companyId?: string;
}) {
  const [season, setSeason] = useState<ActiveSeason | null>(null);
  const [seasons, setSeasons] = useState<SeasonSummary[]>([]);
  const [visibility, setVisibility] = useState<SeasonVisibilityWindow | null>(null);
  const [loading, setLoading] = useState(true);

  // `silent` — обновление без взвода `loading`: фоновый рефетч не должен
  // ронять шапку и попапы в скелетон на каждом возврате во вкладку.
  //
  // Перф-аудит 14.08 (корень 6): три триггера ниже (focus / visibilitychange /
  // таймер) за сессию давали `/session/season` ×124 и `/seasons` ×124 —
  // одинаковые ответы. Focus в браузере срабатывает на каждый возврат в окно,
  // хоть из devtools, и каждый раз это была пара сетевых запросов.
  //
  // Живость сезона (AK-542) сохраняем полностью: триггеры остаются, но проход
  // идёт через общий кэш react-query с окном свежести 5 минут. Фоновый рефетч
  // внутри окна отдаётся из памяти, а два одновременных вызова (focus +
  // visibilitychange приходят парой) схлопываются в один запрос.
  //
  // `force` — только явное действие пользователя (смена сезона, замок
  // редактирования): там ответ обязан быть свежим, ждать окна нельзя.
  const load = useCallback(
    async (silent = false, force = false) => {
      // На публичных страницах сезона нет и быть не может: человек туда пришёл
      // по ссылке из письма, сессии у него ещё нет. Провайдер стоит НАД
      // роутером, поэтому без этой проверки он стучится и оттуда — и получает
      // честный 401. Прод 21.08.2026: этот 401 уводил на `/login` и обрывал
      // навигацию к ручке приглашения (nginx записал 499 — токен уже сгорел,
      // а форма «Придумайте пароль» так и не показалась). Лечим источник, а не
      // только последствие: запрос просто не стартует.
      if (typeof window !== 'undefined' && isPublicPath(window.location.pathname)) {
        setLoading(false);
        return;
      }
      if (!silent) setLoading(true);
      try {
        const [[active, list], config] = await Promise.all([
          fetchSharedSeason<ActiveSeason | null, SeasonSummary[]>(
            fetchActiveSeason,
            () => listSeasons(companyId).catch(() => [] as SeasonSummary[]),
            companyId,
            force,
          ),
          // Окно видимости — фоновая метаинформация: её отказ (штатный 4xx
          // без компании) сезон не роняет, см. fetchSharedSeasonConfig.
          fetchSharedSeasonConfig(() => getSeasonConfig(companyId), companyId, force),
        ]);
        setSeason(active);
        setSeasons(list);
        setVisibility(
          config
            ? { past: config.visible_past_years, future: config.visible_future_years }
            : null,
        );
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [companyId],
  );

  const refetch = useCallback(() => load(false, true), [load]);

  useEffect(() => {
    void load(false);
  }, [load]);

  // AK-542 — сезон живой, а не снимок на момент открытия вкладки.
  //
  // Было: `fetchActiveSeason()` ровно один раз на mount. Вкладка ФМС живёт у
  // агронома неделями, и любое расхождение сезона (смена сезона в соседней
  // вкладке, переход даты через границу сезона, починка данных на сервере)
  // держалось в UI до ручного F5. По нашим принципам UX это усталостный класс
  // багов: «интерфейс, требующий перезагрузки». Именно он растянул AK-542 на
  // два дня — у founder'а замок не отпускало и после исправления на сервере.
  //
  // Три триггера: возврат фокуса в окно, возврат вкладки из фона и таймер на
  // 10 минут (страховка для экрана, который открыт и никогда не теряет фокус —
  // карта на планшете в поле).
  useEffect(() => {
    const onFocus = () => void load(true);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void load(true);
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    const timer = window.setInterval(() => void load(true), 10 * 60 * 1000);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(timer);
    };
  }, [load]);

  const setSeasonByCode = useCallback(
    async (code: string) => {
      await apiSetSeason(code);
      await refetch();
    },
    [refetch],
  );

  const setEditUnlock = useCallback(
    async (on: boolean) => {
      await apiSetEditUnlock(on);
      await refetch();
    },
    [refetch],
  );

  return (
    <SeasonContext.Provider
      value={{
        season,
        seasons,
        visibility,
        loading,
        setSeasonByCode,
        setEditUnlock,
        refetch,
      }}
    >
      {children}
    </SeasonContext.Provider>
  );
}

export function useActiveSeason(): SeasonContextValue {
  const ctx = useContext(SeasonContext);
  if (!ctx) {
    return {
      season: null,
      seasons: [],
      visibility: null,
      loading: false,
      setSeasonByCode: async () => {},
      setEditUnlock: async () => {},
      refetch: async () => {},
    };
  }
  return ctx;
}

/**
 * Замок сезонной линзы. Правило ОДНО на систему и совпадает с серверным
 * гейтом `require_writable_season`: пишем в ЖИВОЙ сезон — текущий или
 * будущий; заперт только ЗАВЕРШЁННЫЙ (канон терминологии v3 §5).
 *
 * AK-542, два дефекта разом:
 *  · `isFuture` фронт не знал вовсе и запирал будущие сезоны, хотя сервер
 *    запись принимал (решение founder 05.08: «отсутствие плана не блокер») —
 *    планирование 2027 из 2026 выглядело заблокированным;
 *  · формулировка «Сезон 2026 не текущий» — не язык агронома и вдобавок
 *    врала: текущесть считалась вторым, неканоничным способом. Говорим то,
 *    что человек может проверить: сезон ЗАВЕРШЁН.
 */
export function useSeasonGuard(): { canWrite: boolean; reason: string | null } {
  const { season } = useActiveSeason();
  if (!season) return { canWrite: true, reason: null };
  if (season.isFuture) return { canWrite: true, reason: null };
  // Тот же вычислитель, что рисует бейдж в SeasonPicker (#699). Замок и
  // бейдж физически не могут разойтись: у них одна функция, а не две формулы.
  // Пока пикер показывает «Идёт» — замок открыт, что бы ни лежало в `isCurrent`.
  const completed = isSeasonCompleted({
    startsOn: season.startsOn,
    endsOn: season.endsOn,
    year: season.year,
    isCurrent: season.isCurrent,
  });
  if (!completed) return { canWrite: true, reason: null };
  if (season.editUnlock) return { canWrite: true, reason: null };
  return {
    canWrite: false,
    reason: `Сезон ${season.year} завершён. Разблокируйте редактирование 🔓 в шапке, чтобы вносить изменения.`,
  };
}
